import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { Snackbar } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetAppResponseBody } from '~/lib/api/modules/Apps';
import type { IUseUpdateAppMutationValues } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import type { IStartupScreenSettingsFormValues } from './StartupScreenSettingsForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import { StartupScreenSettingsForm } from './StartupScreenSettingsForm';

export type IUpdateStartupScreenSettingsFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiGetAppResponseBody,
    inputValues: IUseUpdateAppMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

const SNACKBAR_AUTO_HIDE_DURATION = 3000;

export const UpdateStartupScreenSettingsForm: React.FC<
  IUpdateStartupScreenSettingsFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const appQuery = useAppQuery({ appId });
  const updateAppMutation = useUpdateAppMutation();
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation();

  const app = appQuery.data;

  const handleSubmit: SubmitHandler<IStartupScreenSettingsFormValues> = (
    values,
  ) => {
    if (updateAppMutation.isLoading) {
      return;
    }

    return updateAppMutation.mutateAsync(
      {
        appId,
        body: {
          startupVideo: {
            id: values.videos![0].id,
            mode: values.mode!,
          },
        },
      },
      {
        onSuccess: (outputData, inputValues) => {
          setSubmitted(true);
          onSuccess?.(outputData, inputValues);
        },
      },
    );
  };

  return (
    <>
      <Snackbar
        open={submitted}
        onClose={() => setSubmitted(false)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      >
        {t('common.successfullySaved')}
      </Snackbar>

      <ContentBlock sx={sx}>
        <ContentBlock.Suspense
          loading={appQuery.isLoading}
          error={appQuery.error}
          first
        >
          {app && (
            <StartupScreenSettingsForm
              cancelUrl={cancelUrl}
              defaultValues={{
                videos: app.settings?.startupVideoId
                  ? [
                      {
                        id: app.settings.startupVideoId,
                        type: 'video',
                      },
                    ]
                  : undefined,
                mode: app.settings?.press?.env?.startupVideoMode,
              }}
              onSubmit={handleSubmit}
              appId={appId}
              first
            />
          )}
        </ContentBlock.Suspense>
      </ContentBlock>
    </>
  );
};
